import { useState } from "react";
import { Sparkles, Play, RotateCcw, Check, Film, Megaphone, Zap, ArrowLeft } from "lucide-react";

// MARKETING AI ENGINE — single-screen, mobile-locked creator workspace.
// UI STRUCTURE ONLY. Three layers: pinned header/preview, one scrollable config
// dock, pinned bottom generation tray. Neo-Brutalist: solid black borders, flat
// offset shadows, no gradients/blur. Galaxy S20 chassis: max-w-[412px] / 100dvh.

const HOOK_CHIPS = [
  "POV: you just found the one thing you were missing",
  "Stop scrolling if you want this",
  "3 reasons you need this today",
  "Nobody is talking about this",
  "I tried it so you don't have to",
];

const CTAS = [
  { key: "shop_now", label: "SHOP NOW" },
  { key: "claim_offer", label: "CLAIM OFFER" },
  { key: "learn_more", label: "LEARN MORE" },
  { key: "sign_up", label: "SIGN UP FREE" },
  { key: "book_now", label: "BOOK NOW" },
];

const PACING = [
  { key: "fast_cuts", label: "FAST CUTS", Icon: Zap },
  { key: "storytelling", label: "STORYTELLING", Icon: Film },
];

/* ------------------------------- primitives ------------------------------- */
function Section({ label, hint, children, testid }) {
  return (
    <section className="border-2 border-black bg-white shadow-[4px_4px_0px_#000]" data-testid={testid}>
      <div className="flex items-baseline justify-between border-b-2 border-black bg-cream px-3 py-2">
        <h3 className="font-mono text-[11px] font-black uppercase tracking-widest text-black">{label}</h3>
        {hint && <span className="font-mono text-[8px] uppercase tracking-wider text-black/50 truncate pl-2">{hint}</span>}
      </div>
      <div className="p-3">{children}</div>
    </section>
  );
}

function Chip({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      aria-pressed={active}
      className={`inline-flex max-w-full items-center gap-1 whitespace-normal break-words rounded-none border-2 border-black px-2.5 py-1.5 text-left font-mono text-[10px] font-bold leading-tight shadow-[2px_2px_0px_#000] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none ${
        active ? "bg-gold text-black" : "bg-white text-black"
      }`}
    >
      {active && <Check size={10} strokeWidth={3.5} className="shrink-0" />}
      <span className="min-w-0 break-words">{children}</span>
    </button>
  );
}

/* ------------------------------- workspace -------------------------------- */
export default function MarketingWorkspace({ onGenerate, onClose }) {
  const [hook, setHook] = useState("");
  const [body, setBody] = useState("");
  const [cta, setCta] = useState("shop_now");
  const [pacing, setPacing] = useState("fast_cuts");
  const [status, setStatus] = useState("idle"); // idle | generating | ready

  const stateLabel = { idle: "IDLE", generating: "RENDERING…", ready: "DRAFT READY" }[status];
  const ctaLabel = CTAS.find((c) => c.key === cta)?.label || "SHOP NOW";

  const generate = () => {
    setStatus("generating");
    try { onGenerate?.({ hook, body, cta, pacing }); } catch { /* no-op */ }
    // Local UI-only state animation (no backend / no generation pipeline calls).
    setTimeout(() => setStatus("ready"), 1600);
  };

  const reset = () => {
    setHook(""); setBody(""); setCta("shop_now"); setPacing("fast_cuts"); setStatus("idle");
  };

  return (
    <div
      className="mx-auto flex h-[100dvh] w-full max-w-[412px] flex-col overflow-hidden bg-charcoal"
      data-testid="marketing-workspace"
    >
      {/* ============================ a) PINNED TOP ============================ */}
      <div className="shrink-0 border-b-2 border-black">
        <header className="flex items-center gap-2 px-4 py-2.5">
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              data-testid="mw-back"
              aria-label="Back"
              className="grid h-8 w-8 shrink-0 place-items-center border-2 border-black bg-white text-black shadow-[2px_2px_0px_#000] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none"
            >
              <ArrowLeft size={16} strokeWidth={2.8} />
            </button>
          )}
          <span className="grid h-8 w-8 place-items-center border-2 border-black bg-gold shadow-[2px_2px_0px_#000]">
            <Megaphone size={16} strokeWidth={2.6} className="text-black" />
          </span>
          <div className="min-w-0 leading-tight">
            <p className="font-mono text-[8px] uppercase tracking-[0.24em] text-gold/70">MiSt Studio</p>
            <h1 className="truncate font-mono text-sm font-black uppercase tracking-widest text-gold">Marketing AI Engine</h1>
          </div>
        </header>

        {/* 9:16 media preview simulator — ratio fixed, never distorts */}
        <div className="flex justify-center px-4 pb-3">
          <div
            className="relative aspect-[9/16] h-[26dvh] max-h-[248px] overflow-hidden border-2 border-black bg-black shadow-[4px_4px_0px_#000]"
            data-testid="mw-preview"
          >
            {/* technical overlays */}
            <span
              className="absolute left-1.5 top-1.5 border-2 border-black bg-gold px-1.5 py-0.5 font-mono text-[8px] font-black uppercase tracking-wider text-black"
              data-testid="mw-orientation-chip"
            >
              9:16 · VERTICAL
            </span>
            <span
              className="absolute right-1.5 top-1.5 border-2 border-white/70 bg-black px-1.5 py-0.5 font-mono text-[8px] font-black tracking-wider text-white"
              data-testid="mw-runtime-chip"
            >
              0:15
            </span>

            {/* mock reel composition preview */}
            <div className="flex h-full flex-col justify-between p-2.5 pb-8">
              <p className="line-clamp-3 break-words border-2 border-white/70 bg-black/70 px-1.5 py-1 font-mono text-[10px] font-bold leading-snug text-white">
                {hook || "Your hook appears here…"}
              </p>
              <div className="flex flex-col items-center gap-1.5">
                {status === "generating"
                  ? <Film size={22} className="animate-pulse text-gold" />
                  : <Play size={22} strokeWidth={2.4} className="text-white/50" />}
                <span className="w-full truncate border-2 border-black bg-gold px-2 py-1 text-center font-mono text-[9px] font-black uppercase tracking-widest text-black">
                  {ctaLabel}
                </span>
              </div>
            </div>

            {/* active generation state label */}
            <span
              className={`absolute bottom-1.5 left-1.5 border-2 px-1.5 py-0.5 font-mono text-[8px] font-black uppercase tracking-wider ${
                status === "ready" ? "border-black bg-emerald-400 text-black"
                : status === "generating" ? "border-black bg-gold text-black"
                : "border-white/70 bg-black text-white/80"
              }`}
              data-testid="mw-state-label"
            >
              {stateLabel}
            </span>
          </div>
        </div>
      </div>

      {/* ===================== b) CENTER SCROLL (only scroll) ==================== */}
      <div className="min-h-0 flex-1 space-y-3.5 overflow-y-auto overflow-x-hidden px-4 py-3.5 no-scrollbar" data-testid="mw-config">
        {/* Hook */}
        <Section label="Marketing Hook" hint="opening line" testid="mw-section-hook">
          <input
            value={hook}
            onChange={(e) => setHook(e.target.value)}
            placeholder="Write a scroll-stopping first line…"
            data-testid="mw-hook-input"
            className="mb-2.5 w-full truncate border-2 border-black bg-white px-2.5 py-2 font-mono text-xs text-black placeholder:text-black/35 focus:outline-none"
          />
          <p className="mb-1.5 font-mono text-[8px] uppercase tracking-widest text-black/45">High-conversion patterns</p>
          <div className="flex flex-wrap gap-2">
            {HOOK_CHIPS.map((h, i) => (
              <Chip key={i} active={hook === h} onClick={() => setHook(h)} testid={`mw-hook-chip-${i}`}>{h}</Chip>
            ))}
          </div>
        </Section>

        {/* Body copy */}
        <Section label="Promo Copy" hint="message body" testid="mw-section-body">
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={3}
            placeholder="What's the offer? Keep it punchy for short-form…"
            data-testid="mw-body-input"
            className="w-full resize-none break-words border-2 border-black bg-white px-2.5 py-2 font-mono text-xs leading-relaxed text-black placeholder:text-black/35 focus:outline-none"
          />
        </Section>

        {/* CTA */}
        <Section label="Call To Action" hint="single-select" testid="mw-section-cta">
          <div className="flex flex-wrap gap-2">
            {CTAS.map((c) => (
              <Chip key={c.key} active={cta === c.key} onClick={() => setCta(c.key)} testid={`mw-cta-${c.key}`}>{c.label}</Chip>
            ))}
          </div>
        </Section>

        {/* Pacing */}
        <Section label="Pacing Mode" hint="edit rhythm" testid="mw-section-pace">
          <div className="grid grid-cols-2 gap-2">
            {PACING.map((p) => {
              const active = pacing === p.key;
              return (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => setPacing(p.key)}
                  data-testid={`mw-pace-${p.key}`}
                  aria-pressed={active}
                  className={`flex items-center justify-center gap-1.5 border-2 border-black px-2 py-2.5 font-mono text-[10px] font-black uppercase tracking-widest shadow-[2px_2px_0px_#000] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none ${
                    active ? "bg-gold text-black" : "bg-white text-black"
                  }`}
                >
                  <p.Icon size={13} strokeWidth={2.8} /> {p.label}
                </button>
              );
            })}
          </div>
        </Section>
      </div>

      {/* ======================== c) PINNED BOTTOM TRAY ======================== */}
      <div
        className="shrink-0 border-t-2 border-black bg-charcoal px-4 pt-3"
        style={{ paddingBottom: "max(0.85rem, env(safe-area-inset-bottom))" }}
        data-testid="mw-tray"
      >
        <div className="flex items-stretch gap-2.5">
          <button
            type="button"
            onClick={reset}
            data-testid="mw-reset-btn"
            aria-label="Reset"
            className="grid w-12 shrink-0 place-items-center border-2 border-black bg-white text-black shadow-[3px_3px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none"
          >
            <RotateCcw size={16} strokeWidth={2.6} />
          </button>
          <button
            type="button"
            onClick={generate}
            disabled={status === "generating"}
            data-testid="mw-generate-btn"
            className="flex flex-1 items-center justify-center gap-2 border-2 border-black bg-gold px-3 py-3 font-mono text-[12px] font-black uppercase tracking-widest text-black shadow-[4px_4px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none disabled:opacity-60"
          >
            <Sparkles size={15} strokeWidth={2.8} />
            {status === "generating" ? "Rendering…" : status === "ready" ? "Regenerate Reel" : "Generate Reel"}
          </button>
        </div>
      </div>
    </div>
  );
}
